export type ChartViewMode = 'merged' | 'separate'
export type ChartPanelWidths = { left: number; right: number }

export interface ChartLayoutPreferences {
  order: string[]
  collapsed: string[]
  hidden: string[]
  heights: Record<string, number>
  widths: Record<string, number>
}

export interface ScreenerChartPreset extends ChartLayoutPreferences {
  version: 1
  mode: ChartViewMode
  priceExchange: string
  workspace: {
    widths: ChartPanelWidths
    leftOpen: boolean
    rightOpen: boolean
    sidebarTab: 'discovery' | 'history'
  }
}

const exchangePattern = /^[a-z0-9][a-z0-9_-]{0,79}$/
const chartPattern = /^(price|merged-oi|(?:oi|cvd):[a-z0-9][a-z0-9_-]{0,79})$/
export const DEFAULT_CHART_PANEL_WIDTHS: ChartPanelWidths = { left: 390, right: 360 }

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function stringList(value: unknown, pattern: RegExp, limit = 100): string[] | null {
  return Array.isArray(value) && value.length <= limit && value.every((item) => typeof item === 'string' && pattern.test(item))
    ? [...new Set(value)] : null
}

/** Old layouts ordered exchanges within separate OI and CVD sections. */
export function normalizeChartOrder(value: unknown): string[] | null {
  const charts = stringList(value, chartPattern, 202)
  if (charts) return charts
  const exchanges = stringList(value, exchangePattern)
  if (!exchanges || exchanges.some((id) => id === 'price' || id === 'merged-oi')) return null
  return ['price', 'merged-oi', ...exchanges.map((exchange) => `oi:${exchange}`), ...exchanges.map((exchange) => `cvd:${exchange}`)]
}

function minimumHeight(id: string) {
  return id === 'price' ? 180 : id === 'merged-oi' ? 240 : id.startsWith('cvd:') ? 140 : 120
}

export function normalizeChartLayout(value: unknown): ChartLayoutPreferences | null {
  if (!record(value)) return null
  const order = normalizeChartOrder(value.order)
  const collapsed = stringList(value.collapsed, chartPattern)
  const hidden = value.hidden === undefined ? [] : stringList(value.hidden, chartPattern, 202)
  if (!order || !collapsed || !hidden || !record(value.heights) || Object.keys(value.heights).length > 100) return null
  if (Array.isArray(value.hidden) && value.hidden.length !== hidden.length) return null
  const heights: Record<string, number> = {}
  for (const [id, height] of Object.entries(value.heights)) {
    if (!chartPattern.test(id) || typeof height !== 'number' || !Number.isInteger(height)
      || height < minimumHeight(id) || height > 1000) return null
    heights[id] = height
  }
  const savedWidths = value.widths === undefined ? {} : value.widths
  if (!record(savedWidths) || Object.keys(savedWidths).length > 100) return null
  const widths: Record<string, number> = {}
  for (const [id, width] of Object.entries(savedWidths)) {
    if (!chartPattern.test(id) || typeof width !== 'number' || !Number.isFinite(width) || width < 1 || width > 100) return null
    widths[id] = width
  }
  return { order, collapsed, hidden, heights, widths }
}

export function normalizeChartPanelWidths(value: unknown): ChartPanelWidths | null {
  if (!record(value)) return null
  const { left, right } = value
  if (typeof left !== 'number' || typeof right !== 'number' || !Number.isFinite(left) || !Number.isFinite(right)
    || left < 280 || left > 720 || right < 280 || right > 720) return null
  return { left, right }
}

export function normalizeScreenerChartPreset(value: unknown): ScreenerChartPreset | null {
  if (!record(value) || value.version !== 1 || (value.mode !== 'merged' && value.mode !== 'separate')
    || typeof value.priceExchange !== 'string' || (value.priceExchange !== '' && !exchangePattern.test(value.priceExchange))) return null
  const layout = normalizeChartLayout(value)
  const workspace = value.workspace
  if (!layout || !record(workspace)) return null
  const widths = normalizeChartPanelWidths(workspace.widths)
  if (!widths || typeof workspace.leftOpen !== 'boolean' || typeof workspace.rightOpen !== 'boolean'
    || (workspace.sidebarTab !== 'discovery' && workspace.sidebarTab !== 'history')) return null
  return {
    version: 1, mode: value.mode, priceExchange: value.priceExchange, ...layout,
    workspace: { widths, leftOpen: workspace.leftOpen, rightOpen: workspace.rightOpen, sidebarTab: workspace.sidebarTab },
  }
}

function readStoredValue(key: string): unknown {
  try { return JSON.parse(localStorage.getItem(key) || 'null') } catch { return null }
}

/** Read every saved pane, including exchanges absent from the current ticker. */
export function readChartLayout(storageKey: string): ChartLayoutPreferences {
  const result: ChartLayoutPreferences = { order: [], collapsed: [], hidden: [], heights: {}, widths: {} }
  try {
    const current = normalizeChartLayout(readStoredValue(`${storageKey}:layout`))
    if (current) return current
    result.order = normalizeChartOrder(readStoredValue(storageKey)) || []
    result.collapsed = stringList(readStoredValue(`${storageKey}:collapsed`), chartPattern) || []
    const prefix = `${storageKey}:height:`
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index)
      if (!key?.startsWith(prefix)) continue
      const id = key.slice(prefix.length)
      if (!chartPattern.test(id)) continue
      const height = readStoredValue(key)
      if (typeof height === 'number' && Number.isFinite(height)) {
        result.heights[id] = Math.round(Math.min(1000, Math.max(minimumHeight(id), height)))
      }
    }
  } catch { /* Browser preferences are optional. */ }
  return result
}

export function readChartPanelWidths(storageKey: string): ChartPanelWidths {
  try {
    const saved: unknown = JSON.parse(localStorage.getItem(storageKey) || 'null')
    if (record(saved)) {
      const width = (side: 'left' | 'right') => typeof saved[side] === 'number' && Number.isFinite(saved[side])
        ? Math.min(720, Math.max(280, saved[side])) : DEFAULT_CHART_PANEL_WIDTHS[side]
      return { left: width('left'), right: width('right') }
    }
  } catch { /* Browser preferences are optional. */ }
  return { ...DEFAULT_CHART_PANEL_WIDTHS }
}
